import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Button, Spinner, Alert, Form, Badge, InputGroup } from 'react-bootstrap';
import { adminAPI } from '../../services/apiService';
import { getTranslatedError } from '../../utils/errorTranslator';
import { BarChartOutlined, ReloadOutlined, CalendarOutlined, UserOutlined } from '@ant-design/icons';
import { message } from 'antd';

const Statistics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Статистика по авторам
  const [authorsStats, setAuthorsStats] = useState(null);
  const [protocolsByPeriod, setProtocolsByPeriod] = useState(null);
  
  // Фильтры
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('day');
  const [useCustomDates, setUseCustomDates] = useState(false);

  useEffect(() => {
    if (!useCustomDates || (useCustomDates && startDate && endDate)) {
      loadStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, startDate, endDate, groupBy, useCustomDates]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError('');

      // Загружаем статистику по авторам
      let authorsParams;
      if (useCustomDates && startDate && endDate) {
        // Преобразуем локальные даты в ISO формат
        const startISO = new Date(startDate).toISOString();
        const endISO = new Date(endDate).toISOString();
        authorsParams = { startDate: startISO, endDate: endISO };
      } else {
        authorsParams = { period };
      }
      
      const authorsData = await adminAPI.getAuthorsStats(authorsParams);
      console.log('Authors stats received:', authorsData);
      
      // Обрабатываем данные: API возвращает { totals: { total, completed, failed }, byAuthor: [...] }
      if (authorsData) {
        // Извлекаем общую статистику из totals
        const totals = authorsData.totals || {};
        const total = totals.total || authorsData.total || authorsData.totalTasks || 0;
        const completed = totals.completed || authorsData.completed || authorsData.completedTasks || 0;
        const failed = totals.failed || authorsData.failed || authorsData.failedTasks || 0;
        
        // Извлекаем список авторов из byAuthor
        const byAuthor = authorsData.byAuthor || [];
        // Преобразуем формат для совместимости с компонентом
        const authors = byAuthor.map(item => ({
          user: item.user,
          total: item.statistics?.total || 0,
          completed: item.statistics?.completed || 0,
          failed: item.statistics?.failed || 0,
        }));
        
        setAuthorsStats({
          total,
          completed,
          failed,
          authors,
        });
      } else {
        // Если данных нет, устанавливаем нули
        setAuthorsStats({
          total: 0,
          completed: 0,
          failed: 0,
          authors: [],
        });
      }

      // Также загружаем общую статистику по задачам для проверки
      try {
        const tasksStats = await adminAPI.getTasksStats();
        console.log('Tasks stats received:', tasksStats);
        // Если общая статистика есть и она отличается, обновляем
        if (tasksStats && (tasksStats.total || tasksStats.totalTasks)) {
          setAuthorsStats(prev => ({
            ...prev,
            total: tasksStats.total || tasksStats.totalTasks || prev.total || 0,
            completed: tasksStats.completed || tasksStats.completedTasks || prev.completed || 0,
            failed: tasksStats.failed || tasksStats.failedTasks || prev.failed || 0,
          }));
        }
      } catch (err) {
        console.warn('Failed to load tasks stats:', err);
      }

      // Загружаем статистику по периодам
      let periodParams;
      if (useCustomDates && startDate && endDate) {
        const startISO = new Date(startDate).toISOString();
        const endISO = new Date(endDate).toISOString();
        periodParams = {
          startDate: startISO,
          endDate: endISO,
          groupBy,
        };
      } else {
        // Для предустановленных периодов вычисляем даты
        const dates = getPeriodDates(period);
        if (dates) {
          periodParams = {
            startDate: dates.start,
            endDate: dates.end,
            groupBy: period === 'year' ? 'month' : period === 'month' ? 'day' : 'day',
          };
        }
      }
      
      if (periodParams) {
        const periodData = await adminAPI.getProtocolsByPeriod(periodParams);
        setProtocolsByPeriod(periodData);
      }
    } catch (err) {
      setError('Ошибка при загрузке статистики: ' + getTranslatedError(err));
      message.error('Ошибка при загрузке статистики: ' + getTranslatedError(err));
    } finally {
      setLoading(false);
    }
  };

  const getPeriodDates = (periodType) => {
    const now = new Date();
    let start, end;

    switch (periodType) {
      case 'day':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'week':
        const dayOfWeek = now.getDay();
        const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        start = new Date(now.setDate(diff));
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        break;
      default:
        return null;
    }

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatPeriodLabel = (period, groupBy) => {
    if (!period) return '—';
    
    switch (groupBy) {
      case 'day':
        return formatDate(period);
      case 'week':
        return `Неделя ${period}`;
      case 'month':
        const [year, month] = period.split('-');
        return new Date(year, month - 1).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
      case 'year':
        return period;
      default:
        return period;
    }
  };

  return (
    <div>
      {/* Фильтры */}
      <Card className="mb-2">
        <Card.Header className="p-2">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0" style={{ fontSize: '16px' }}>
              <BarChartOutlined className="me-2" />
              Статистика по протоколам
            </h6>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={loadStatistics}
              disabled={loading}
              style={{ fontSize: '12px', padding: '4px 8px' }}
            >
              <ReloadOutlined /> Обновить
            </Button>
          </div>
        </Card.Header>
        <Card.Body className="p-2">
          <Row className="g-2 mb-2">
            <Col md={12}>
              <Form.Check
                type="switch"
                id="custom-dates-switch"
                label="Использовать произвольный период"
                checked={useCustomDates}
                onChange={(e) => setUseCustomDates(e.target.checked)}
                style={{ fontSize: '13px' }}
              />
            </Col>
          </Row>

          {!useCustomDates ? (
            <Row className="g-2">
              <Col md={4}>
                <Form.Label className="fw-bold" style={{ fontSize: '13px' }}>
                  <CalendarOutlined className="me-2" />
                  Период
                </Form.Label>
                <Form.Select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  size="sm"
                >
                  <option value="day">День</option>
                  <option value="week">Неделя</option>
                  <option value="month">Месяц</option>
                  <option value="year">Год</option>
                </Form.Select>
              </Col>
            </Row>
          ) : (
            <Row className="g-2">
              <Col md={4}>
                <Form.Label className="fw-bold" style={{ fontSize: '13px' }}>
                  Начальная дата
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="sm"
                  required
                />
              </Col>
              <Col md={4}>
                <Form.Label className="fw-bold" style={{ fontSize: '13px' }}>
                  Конечная дата
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  size="sm"
                  required
                />
              </Col>
              <Col md={3}>
                <Form.Label className="fw-bold" style={{ fontSize: '13px' }}>
                  Группировка
                </Form.Label>
                <Form.Select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  size="sm"
                >
                  <option value="day">По дням</option>
                  <option value="week">По неделям</option>
                  <option value="month">По месяцам</option>
                  <option value="year">По годам</option>
                </Form.Select>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Загрузка статистики...</p>
        </div>
      ) : (
        <>
          {/* Общая статистика */}
          {authorsStats && (
            <Row className="mb-2 g-2">
              <Col md={4}>
                <Card className="text-center border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <Card.Body className="p-2">
                    <h4 className="mb-0" style={{ fontSize: '20px' }}>{authorsStats.total || 0}</h4>
                    <p className="mb-0" style={{ fontSize: '11px' }}>Всего протоколов</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="text-center border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)', color: 'white' }}>
                  <Card.Body className="p-2">
                    <h4 className="mb-0" style={{ fontSize: '20px' }}>{authorsStats.completed || 0}</h4>
                    <p className="mb-0" style={{ fontSize: '11px' }}>Завершено</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="text-center border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)', color: 'white' }}>
                  <Card.Body className="p-2">
                    <h4 className="mb-0" style={{ fontSize: '20px' }}>{authorsStats.failed || 0}</h4>
                    <p className="mb-0" style={{ fontSize: '11px' }}>Ошибок</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          <Row>
            {/* Статистика по авторам */}
            <Col md={6}>
              <Card className="mb-2">
                <Card.Header className="p-2">
                  <h6 className="mb-0" style={{ fontSize: '15px' }}>
                    <UserOutlined className="me-2" />
                    Статистика по авторам
                  </h6>
                </Card.Header>
                <Card.Body className="p-2">
                  {authorsStats && authorsStats.authors && authorsStats.authors.length > 0 ? (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th style={{ fontSize: '12px' }}>Автор</th>
                            <th style={{ fontSize: '12px' }} className="text-center">Всего</th>
                            <th style={{ fontSize: '12px' }} className="text-center">Завершено</th>
                            <th style={{ fontSize: '12px' }} className="text-center">Ошибок</th>
                          </tr>
                        </thead>
                        <tbody>
                          {authorsStats.authors.map((author, idx) => (
                            <tr key={author.user?.id || author.id || idx}>
                              <td style={{ fontSize: '12px' }}>
                                <div>
                                  <strong>{author.user?.login || author.login || '—'}</strong>
                                  {author.user?.name && (
                                    <div style={{ fontSize: '11px', color: '#6c757d' }}>
                                      {author.user.name}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="text-center">
                                <Badge bg="primary" style={{ fontSize: '11px' }}>
                                  {author.total || 0}
                                </Badge>
                              </td>
                              <td className="text-center">
                                <Badge bg="success" style={{ fontSize: '11px' }}>
                                  {author.completed || 0}
                                </Badge>
                              </td>
                              <td className="text-center">
                                <Badge bg="danger" style={{ fontSize: '11px' }}>
                                  {author.failed || 0}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <Alert variant="info" className="mb-0">
                      Нет данных за выбранный период
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Протоколы по периодам */}
            <Col md={6}>
              <Card className="mb-2">
                <Card.Header className="p-2">
                  <h6 className="mb-0" style={{ fontSize: '15px' }}>
                    <BarChartOutlined className="me-2" />
                    Протоколы по периодам
                  </h6>
                </Card.Header>
                <Card.Body className="p-2">
                  {protocolsByPeriod ? (
                    <div>
                      <div className="mb-2 text-center">
                        <h5 className="mb-0" style={{ fontSize: '20px', color: '#1890ff' }}>
                          {protocolsByPeriod.total || 0}
                        </h5>
                        <p className="text-muted mb-0" style={{ fontSize: '11px' }}>
                          Всего протоколов за период
                        </p>
                      </div>

                      {protocolsByPeriod.byPeriod && protocolsByPeriod.byPeriod.length > 0 ? (
                        <div className="table-responsive">
                          <Table striped bordered hover size="sm">
                            <thead>
                              <tr>
                                <th style={{ fontSize: '12px' }}>Период</th>
                                <th style={{ fontSize: '12px' }} className="text-center">Количество</th>
                                <th style={{ fontSize: '12px' }}>График</th>
                              </tr>
                            </thead>
                            <tbody>
                              {protocolsByPeriod.byPeriod.map((item, idx) => {
                                const maxCount = Math.max(...protocolsByPeriod.byPeriod.map(p => p.count || 0));
                                const percentage = maxCount > 0 ? ((item.count || 0) / maxCount) * 100 : 0;
                                
                                return (
                                  <tr key={idx}>
                                    <td style={{ fontSize: '12px' }}>
                                      {formatPeriodLabel(item.period, groupBy)}
                                    </td>
                                    <td className="text-center">
                                      <Badge bg="info" style={{ fontSize: '11px' }}>
                                        {item.count || 0}
                                      </Badge>
                                    </td>
                                    <td>
                                      <div
                                        style={{
                                          width: '100%',
                                          height: '20px',
                                          backgroundColor: '#e0e0e0',
                                          borderRadius: '4px',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: `${percentage}%`,
                                            height: '100%',
                                            background: 'linear-gradient(90deg, #1890ff 0%, #52c41a 100%)',
                                            transition: 'width 0.3s',
                                          }}
                                        />
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <Alert variant="info" className="mb-0">
                          Нет данных за выбранный период
                        </Alert>
                      )}
                    </div>
                  ) : (
                    <Alert variant="info" className="mb-0">
                      Выберите период для отображения статистики
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default Statistics;

